import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EmailTemplateEditor = ({ selectedTemplate, onSaveTemplate, onError }) => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    console.log('=== DEBUG: EmailTemplateEditor useEffect ===');
    console.log('selectedTemplate:', selectedTemplate);
    if (selectedTemplate) {
      console.log('selectedTemplate.templateId:', selectedTemplate.templateId);
      console.log('selectedTemplate.name:', selectedTemplate.name);
      console.log('selectedTemplate.type:', selectedTemplate.type);
      console.log('selectedTemplate.isNew:', selectedTemplate.isNew);
      setSubject(selectedTemplate.subject || '');
      setContent(selectedTemplate.content || '');
    } else {
      setSubject('');
      setContent('');
    }
    console.log('=== END DEBUG ===');
  }, [selectedTemplate]);

  const handleSave = async () => {
    console.log('=== DEBUG: Handle Save ===');
    console.log('selectedTemplate:', selectedTemplate);
    
    if (!selectedTemplate) {
      if (onError) {
        onError('Please select a template first');
      }
      return;
    }

    setIsSaving(true);
    try {
      const templateData = {
        templateId: selectedTemplate.templateId,
        name: selectedTemplate.name,
        type: selectedTemplate.type,
        subject,
        content,
        isActive: true
      };

      console.log('Template data being saved:', templateData);
      console.log('templateData.templateId:', templateData.templateId);

      // Include id if it exists (for existing templates)
      if (selectedTemplate.id && typeof selectedTemplate.id === 'number') {
        templateData.id = selectedTemplate.id;
      }

      console.log('Final template data:', templateData);
      
      await onSaveTemplate(templateData);
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const insertPlaceholder = (placeholder) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      if (range) {
        quill.insertText(range.index, `{{${placeholder}}}`, 'user');
      }
    }
  };

  // Quill editor modules and formats
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align',
    'link', 'image'
  ];

  // Common placeholders for email templates
  const commonPlaceholders = [
    { label: 'User Name', value: 'user_name' },
    { label: 'Company Name', value: 'company_name' },
    { label: 'Reset Link', value: 'reset_link' },
    { label: 'Verification Link', value: 'verification_link' },
    { label: 'Confirmation Link', value: 'confirmation_link' },
    { label: 'Deletion Link', value: 'deletion_link' },
    { label: 'New Email', value: 'new_email' },
    { label: 'Expiry Time', value: 'expiry_time' },
    { label: 'Current Date', value: 'current_date' },
    { label: 'Support Email', value: 'support_email' }
  ];

  // Task-specific placeholders (task assignment notification)
  const taskPlaceholders = [
    { label: 'Assigned To', value: 'assigned_user' },
    { label: 'Task Title', value: 'task_title' },
    { label: 'Task Type', value: 'task_type' },
    { label: 'Priority', value: 'priority' },
    { label: 'Status', value: 'status' },
    { label: 'Description', value: 'description' },
    { label: 'Due Date', value: 'due_date' },
    { label: 'Client Name', value: 'client_name' },
    { label: 'Project Name', value: 'project_name' },
    { label: 'Contact Name', value: 'contact_name' },
    { label: 'Created By', value: 'created_by' },
    { label: 'Created At', value: 'created_at' }
  ];

  // Lead-specific placeholders (lead assignment notification)
  const leadPlaceholders = [
    { label: 'Assigned To', value: 'assigned_user' },
    { label: 'Project Name', value: 'project_name' },
    { label: 'Company Name', value: 'company_name' },
    { label: 'Contact Name', value: 'contact_name' },
    { label: 'Email', value: 'email' },
    { label: 'Stage', value: 'stage' },
    { label: 'Sector', value: 'sector' },
    { label: 'Project Description', value: 'project_description' },
    { label: 'Created By', value: 'created_by' },
    { label: 'Created At', value: 'created_at' }
  ];

  // Invoice-specific placeholders
  const invoicePlaceholders = [
    { label: 'Invoice Number', value: 'invoice_number' },
    { label: 'Client Name', value: 'client_name' },
    { label: 'Proposal Code', value: 'proposal_code' },
    { label: 'Service Description', value: 'description' },
    { label: 'Issue Date', value: 'issue_date' },
    { label: 'Due Date', value: 'due_date' },
    { label: 'Amount Due', value: 'amount_due' },
    { label: 'Invoice Notes', value: 'invoice_notes' },
    { label: 'Message', value: 'message' },
    { label: 'Payment Method', value: 'payment_method' },
    { label: 'Company Name', value: 'company_name' },
    { label: 'Company Email', value: 'company_email' },
    { label: 'PIC Name', value: 'pic_name' },
    { label: 'PIC Email', value: 'pic_email' },
    { label: 'Creator Name', value: 'creator_name' },
    { label: 'Assigned To', value: 'assigned_user_name' }
  ];

  // Task reminder specific placeholders (for due today/tomorrow reminders)
  const taskReminderPlaceholders = [
    { label: 'Assigned To', value: 'assigned_user' },
    { label: 'Task Title', value: 'task_title' },
    { label: 'Task Type', value: 'task_type' },
    { label: 'Priority', value: 'priority' },
    { label: 'Status', value: 'status' },
    { label: 'Description', value: 'description' },
    { label: 'Due Date', value: 'due_date' },
    { label: 'Client Name', value: 'client_name' },
    { label: 'Project Name', value: 'project_name' },
    { label: 'Contact Name', value: 'contact_name' },
    { label: 'Urgency Message', value: 'urgency_message' },
    { label: 'Reminder Period', value: 'reminder_period' },
    { label: 'Urgency Level', value: 'urgency_level' },
    { label: 'Is Due Today', value: 'is_due_today' },
    { label: 'Is Due Tomorrow', value: 'is_due_tomorrow' },
    { label: 'Current Date', value: 'current_date' },
    { label: 'Current DateTime', value: 'current_datetime' }
  ];

  const invoiceOverdueReminderPlaceholders = [
    { label: 'Assigned To', value: 'assigned_user' },
    { label: 'Invoice Count', value: 'invoice_count' },
    { label: 'Invoice Table (HTML)', value: 'invoice_table' },
    { label: 'Current Date', value: 'current_date' }
  ];

  // Determine which placeholders to show based on template type
  const templateId = selectedTemplate?.templateId;
  const placeholders =
    templateId === 'invoice' ? invoicePlaceholders :
    templateId === 'task' ? taskPlaceholders :
    templateId === 'lead' ? leadPlaceholders :
    templateId === 'invoice_overdue_reminder' ? invoiceOverdueReminderPlaceholders :
    (templateId === 'task_reminder_today' || templateId === 'task_reminder_tomorrow') ? taskReminderPlaceholders :
    commonPlaceholders;

  if (!selectedTemplate) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Email Template Editor</h3>
        </div>
        <div className="card-body">
          <div className="text-center text-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            <p className="mt-3">Select a template from the left sidebar to start editing</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="card-title">
          {selectedTemplate.name}
        </h3>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            'Save Template'
          )}
        </button>
      </div>
      <div className="card-body">
        {/* Subject Field */}
        <div className="mb-3">
          <label className="form-label fw-bold">Subject</label>
          <input
            type="text"
            className="form-control"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter email subject..."
          />
        </div>

        {/* Placeholders */}
        <div className="mb-3">
          <label className="form-label fw-bold">Placeholders</label>
          <div className="d-flex flex-wrap gap-2">
            {placeholders.map((placeholder) => (
              <button
                key={placeholder.value}
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => insertPlaceholder(placeholder.value)}
                title={`Insert ${placeholder.label}`}
              >
                {placeholder.label}
              </button>
            ))}
          </div>
          <small className="text-muted">
            Click on a placeholder to insert it into the email body
          </small>
          
          {/* Show example template for invoice type */}
          {selectedTemplate?.templateId === 'invoice' && (
            <div className="mt-3">
              <div className="alert alert-info">
                <h6>📧 Example Invoice Template:</h6>
                <pre className="mb-0" style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
{`Dear Finance Team,

We would like to request the issuance of an invoice with the following details:

**Client Name**: {{client_name}}
**Proposal No.**: {{proposal_code}}
**Service Description**: {{description}}
**Invoice Amount**: {{amount_due}}
**Due Date**: {{due_date}}
**Message**: {{invoice_notes}}

Attached are the supporting documents for invoice issuance:

* Approved proposal / contract

Please proceed with the invoice issuance and let us know once the invoice has been generated.

Thank you for your support.

Best regards,
{{assigned_user_name}}`}
                </pre>
              </div>
            </div>
          )}

          {/* Show example template for task type */}
          {selectedTemplate?.templateId === 'task' && (
            <div className="mt-3">
              <div className="alert alert-info">
                <h6>📧 Example Task Assignment Template:</h6>
                <pre className="mb-0" style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
{`Subject: New Task Assigned: {{task_title}}

Dear {{assigned_user}},

You have been assigned a new task:

Task Title: {{task_title}}
Type: {{task_type}}
Priority: {{priority}}
Status: {{status}}
Due Date: {{due_date}}

Client: {{client_name}}
Project: {{project_name}}
Contact: {{contact_name}}

Description:
{{description}}

Assigned by {{created_by}} on {{created_at}}.`}
                </pre>
              </div>
            </div>
          )}

          {/* Show example template for lead type */}
          {selectedTemplate?.templateId === 'lead' && (
            <div className="mt-3">
              <div className="alert alert-info">
                <h6>📧 Example Lead Assignment Template:</h6>
                <pre className="mb-0" style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
{`Subject: New Lead Assigned: {{project_name}}

Dear {{assigned_user}},

You have been assigned a new lead:

Project Name: {{project_name}}
Company Name: {{company_name}}
Contact Name: {{contact_name}}
Email: {{email}}
Stage: {{stage}}
Sector: {{sector}}

Project Description:
{{project_description}}

Assigned by {{created_by}} on {{created_at}}.`}
                </pre>
              </div>
            </div>
          )}

          {/* Show example template for task reminder today */}
          {selectedTemplate?.templateId === 'task_reminder_today' && (
            <div className="mt-3">
              <div className="alert alert-danger">
                <h6>Example Task Reminder (Due Today) Template:</h6>
                <pre className="mb-0" style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
{`Subject: Task Due TODAY - {{task_title}}

Dear {{assigned_user}},

{{urgency_message}}

Task Title: {{task_title}}
Type: {{task_type}}
Priority: {{priority}}
Status: {{status}}
Due Date: {{due_date}}
Client: {{client_name}}
Project: {{project_name}}
Contact: {{contact_name}}
Description: {{description}}

Please take immediate action to complete this task or update its status if it has already been completed.

You can access the task in the CRM system to view more details and update its progress.

Best regards,
CRM System`}
                </pre>
              </div>
            </div>
          )}

          {/* Show example template for task reminder tomorrow */}
          {selectedTemplate?.templateId === 'task_reminder_tomorrow' && (
            <div className="mt-3">
              <div className="alert alert-warning">
                <h6>Example Task Reminder (Due Tomorrow) Template:</h6>
                <pre className="mb-0" style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
{`Subject: Task Due Tomorrow - {{task_title}}

Dear {{assigned_user}},

{{urgency_message}}

Task Title: {{task_title}}
Type: {{task_type}}
Priority: {{priority}}
Status: {{status}}
Due Date: {{due_date}}
Client: {{client_name}}
Project: {{project_name}}
Contact: {{contact_name}}
Description: {{description}}

Please plan to complete this task by tomorrow to avoid any delays.

You can access the task in the CRM system to view more details and update its progress.

Best regards,
CRM System`}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Email Body Editor */}
        <div className="mb-3">
          <label className="form-label fw-bold">Email Body</label>
          <div className="border rounded">
            <ReactQuill
              ref={quillRef}
              value={content}
              onChange={setContent}
              modules={modules}
              formats={formats}
              placeholder="Write your email content here..."
              style={{ height: '300px' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateEditor; 